// ============================================
// ProtectedRoute: AuthProvider ベースの認証ガード
// Supabase Auth onAuthStateChange でリアクティブに認証状態を検知
// ============================================
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
// SPEC-PRIZE-RANKING-STAFF-ACCESS-V1 (D-600): ロール表は roles.js に 1 つだけ置く。
import { STAFF_REPORT_ROLES, MANAGER_ROLES, ADMIN_ONLY_ROLES } from '../shared/auth/roles'
// SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838): 押し戻した「場所と理由」を残す。★推測で直さないための記録。
import { traceAuth, TRACE } from '../lib/authTrace'

// 未認証時は React ログイン画面へリダイレクト
// ★D-838: ここは window.location.href = 全ページ再読込。React の遷移ではないので
//   画面の状態が丸ごと捨てられ、名前一覧からやり直しになる(症状 C そのもの)。
//   どのガードが、どの認証状態を見てここへ来たのかを残す。
function redirectToPortal(where, auth) {
  traceAuth(TRACE.guardRedirect, where, {
    loading: auth?.loading, isLoggedIn: auth?.isLoggedIn, offline: auth?.offline,
  })
  window.location.href = '/login'
  return null
}

/**
 * 認証必須ルート
 */
export default function ProtectedRoute({ children }) {
  const auth = useAuth()
  const { loading, isLoggedIn } = auth
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100dvh',color:'#888'}}>認証中...</div>
  if (!isLoggedIn) return redirectToPortal('protected_route', auth)
  return children
}

/**
 * ロール必須ルート（認証 + ロールチェック）
 * @param {string[]} roles - 許可ロール
 * @param {string} [fallback='/'] - 権限不足時のリダイレクト先
 */
export function RoleRoute({ roles, fallback = '/', children }) {
  const auth = useAuth()
  const { loading, isLoggedIn, staffRole } = auth
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100dvh',color:'#888'}}>認証中...</div>
  if (!isLoggedIn) return redirectToPortal('role_route', auth)
  // ★D-838: 役割不足は /login ではなく fallback への React 遷移で、追っている押し戻し(症状 C)とは別物。
  //   ここは D-600 が「許可外は fallback へ」を現物で固定しているので **1 文字も変えない**。
  if (!roles.includes(staffRole)) return <Navigate to={fallback} replace />
  return children
}

// プリセット
// D-992: 許可ロールを roles.js の ADMIN_ONLY_ROLES へ寄せた(中身は ['admin'] のまま。既存の AdminRoute 全ルートの挙動は不変)。
export function AdminRoute({ children }) { return <RoleRoute roles={ADMIN_ONLY_ROLES}>{children}</RoleRoute> }
// SPEC-COLLECTION-SCHEDULE-MANAGER-ACCESS-01 (D-701): 許可ロールを roles.js の 1 箇所へ寄せた。
//   ★ハブのタイルの出し分けが同じ MANAGER_ROLES を見るので、片方だけ変わることがなくなる。
//   ★中身は ['admin','manager'] のまま。既存の ManagerRoute 全ルートの挙動は 1 ビットも変わらない。
// SPEC-ARRIVAL-ADMIN-ONLY-01 (D-899): fallback を渡せるようにした(未指定なら従来どおり RoleRoute 既定の '/')。
//   ★既存の全呼び出し元(fallback を渡さない ~30 箇所)は挙動不変(undefined → RoleRoute の default '/' がそのまま効く)。
export function ManagerRoute({ children, fallback }) { return <RoleRoute roles={MANAGER_ROLES} fallback={fallback}>{children}</RoleRoute> }
export function PatrolRoute({ children }) { return <RoleRoute roles={['admin', 'manager', 'patrol']}>{children}</RoleRoute> }
// SPEC-RBAC-CONTRACTOR-COLLECTION-01 (D-312) S4: 集金画面を contractor にも開放(在店集金)。patrol/staff は従来どおり不可。
export function CollectionRoute({ children }) { return <RoleRoute roles={['admin', 'manager', 'contractor']}>{children}</RoleRoute> }
// SPEC-PRIZE-RANKING-STAFF-ACCESS-V1 (D-600): 現場で運用/設定する人が見る参照系のレポート。
//   ★ひろ指示「景品ランキングは運用する人間設定する人間が参照したい」。admin + manager + staff。
//   ★patrol / contractor は含めない(外部委託に見せる意図は無い)。
export function StaffReportRoute({ children }) { return <RoleRoute roles={STAFF_REPORT_ROLES}>{children}</RoleRoute> }
